// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Three sequential graph-building passes: computeDistances, scaleGraph, applyStyles.

import { TreeTypes } from "./constants.js";
import { getBranchScale, getStepScale } from "./scales.js";

// Pass 1 — computeDistances
// Sets node.distanceFromRoot as cumulative branch length from root (top-down).
// Also handles collapse state (props.collapsedIds) and collects visible leaves.
export function computeDistances(graph, props) {
  const collapsedIds = new Set(props.collapsedIds || []);

  // Apply collapse state top-down (skip root at index 0)
  for (let i = 1; i < graph.preorderTraversal.length; i++) {
    const node = graph.preorderTraversal[i];
    node.isCollapsed = collapsedIds.has(node.id);
    node.isHidden = node.parent.isCollapsed || node.parent.isHidden;
  }

  // Recompute visibleLeaves bottom-up after collapse
  for (let i = 0; i < graph.postorderTraversal.length; i++) {
    const node = graph.postorderTraversal[i];
    if (node.isLeaf) {
      node.visibleLeaves = 1;
    } else if (node.isCollapsed) {
      node.visibleLeaves = 1;
    } else {
      node.visibleLeaves = 0;
      for (const child of node.children) {
        node.visibleLeaves += child.visibleLeaves;
      }
    }
  }

  // Determine effective root (supports props.rootId rerooting)
  const root = graph.nodeById[props.rootId] || graph.preorderTraversal[0];
  root.distanceFromRoot = 0;

  for (let i = 1; i < root.totalNodes; i++) {
    const node = graph.preorderTraversal[root.preIndex + i];
    node.distanceFromRoot =
      node.parent.distanceFromRoot + node.branchLength;
  }

  // Collect visible leaf nodes (skip collapsed subtrees)
  const leaves = [];
  for (let i = 1; i < root.totalNodes; i++) {
    const node = graph.preorderTraversal[root.preIndex + i];
    if (node.isLeaf) {
      leaves.push(node);
    }
    if (node.isCollapsed) {
      i += node.totalNodes - 1;
    }
  }

  graph.root = root;
  graph.leaves = leaves;
  graph.firstIndex = root.preIndex;
  graph.lastIndex = root.preIndex + root.totalNodes;
}

// Pass 2 — scaleGraph
// Converts the layout-space bx/by coordinates into pixel-space x/y.
// Also sets node.dist (distanceFromRoot * branchScale) and for circular
// layouts sets node.cx / node.cy from bcx/bcy.
function scaleBounds(bounds, xScale, yScale) {
  return {
    min: [bounds.min[0] * xScale, bounds.min[1] * yScale],
    max: [bounds.max[0] * xScale, bounds.max[1] * yScale],
  };
}

export function scaleGraph(graph, props, drawingArea) {
  const type = props.type;
  const branchScale = getBranchScale(graph, type, drawingArea, props.branchZoom || 0);
  const stepScale = getStepScale(graph, type, drawingArea, props.stepZoom || 0);

  graph.branchScale = branchScale;
  graph.stepScale = stepScale;

  const firstIndex = graph.root.postIndex - graph.root.totalNodes + 1;
  const lastIndex = graph.root.postIndex;

  if (type === TreeTypes.Circular) {
    for (let i = 0; i < graph.root.totalNodes; i++) {
      const node = graph.preorderTraversal[graph.root.preIndex + i];
      node.x = node.bx * branchScale;
      node.y = node.by * branchScale;
      node.dist = node.distanceFromRoot * branchScale;
      node.cx = node.bcx * branchScale;
      node.cy = node.bcy * branchScale;
    }
    graph.bounds = scaleBounds(graph.bounds, branchScale, branchScale);
    return;
  }

  if (type === TreeTypes.Diagonal) {
    for (let i = 0; i < graph.root.totalNodes; i++) {
      const node = graph.preorderTraversal[graph.root.preIndex + i];
      node.x = node.bx * stepScale;
      node.y = node.by * stepScale;
    }
    graph.bounds = scaleBounds(graph.bounds, stepScale, stepScale);
    return;
  }

  if (type === TreeTypes.Hierarchical) {
    for (let i = firstIndex; i <= lastIndex; i++) {
      const node = graph.postorderTraversal[i];
      node.x = node.bx * stepScale;
      node.y = node.by * branchScale;
    }
    graph.bounds = scaleBounds(graph.bounds, stepScale, branchScale);
    return;
  }

  if (type === TreeTypes.Radial) {
    for (let i = 0; i < graph.root.totalNodes; i++) {
      const node = graph.preorderTraversal[graph.root.preIndex + i];
      node.x = node.bx * branchScale;
      node.y = node.by * branchScale;
    }
    graph.bounds = scaleBounds(graph.bounds, branchScale, branchScale);
    return;
  }

  if (type === TreeTypes.Rectangular) {
    for (let i = firstIndex; i <= lastIndex; i++) {
      const node = graph.postorderTraversal[i];
      node.x = node.bx * branchScale;
      node.y = node.by * stepScale;
    }
    graph.bounds = scaleBounds(graph.bounds, branchScale, stepScale);
    return;
  }

  throw new Error(`scaleGraph: unrecognised tree type "${type}"`);
}

// Pass 3 — applyStyles
// Sets node.shape, node.fillColour, node.strokeColour, node.label for every
// visible node.  Per-node overrides come from props.styles[node.id].
// Colours are kept as [r, g, b, a] arrays — no CSS conversion.
function normaliseColour(value) {
  if (Array.isArray(value)) return value;
  // Defensive fallback: unknown string colours become opaque black
  return [0, 0, 0, 255];
}

export function applyStyles(graph, props) {
  const styles = props.styles || {};
  const defaultNodeShape = props.nodeShape != null ? props.nodeShape : "circle";
  const defaultFill = normaliseColour(props.fillColour || [51, 51, 51, 255]);
  const defaultStroke = normaliseColour(props.strokeColour || [34, 34, 34, 255]);
  const styleNodeEdges = props.styleNodeEdges || false;
  const showPiecharts = props.showPiecharts || false;

  const NO_STYLE = {};
  const firstIndex = graph.root.preIndex;
  const lastIndex = graph.root.preIndex + graph.root.totalNodes;

  for (let i = firstIndex; i < lastIndex; i++) {
    const node = graph.preorderTraversal[i];

    if (node.isLeaf) {
      const style = styles[node.id] || NO_STYLE;
      node.shape = style.shape !== undefined ? style.shape : defaultNodeShape;
      node.fillColour = style.fillColour
        ? normaliseColour(style.fillColour)
        : defaultFill;
      node.strokeColour =
        styleNodeEdges && style.strokeColour
          ? normaliseColour(style.strokeColour)
          : defaultStroke;
      node.label = style.label !== undefined ? style.label : node.id;
    }

    if (!showPiecharts && node.isCollapsed) {
      i += node.totalNodes - 1;
    }
  }

  // Propagate edge colour up internal nodes when styleNodeEdges is active
  if (styleNodeEdges) {
    const firstPostIndex = graph.root.postIndex - graph.root.totalNodes + 1;
    const lastPostIndex = graph.root.postIndex;

    for (let i = firstPostIndex; i <= lastPostIndex; i++) {
      const node = graph.postorderTraversal[i];
      if (!node.isLeaf) {
        node.strokeColour = node.children[0].strokeColour;
        for (const child of node.children) {
          if (child.strokeColour !== node.strokeColour) {
            node.strokeColour = defaultStroke;
            break;
          }
        }
      }
    }
  }
}
